import type { Riddle, LetterValidation, GameState, ShareData } from './types';
import { checkWordInDictionary } from './dictionary';
import riddlesData from './data/riddles.json';

// Constants
export const BOARD_ROWS = 4;
export const BOARD_COLS = 6;
export const ORIGIN_DATE = new Date('2022-12-13');

// Import riddles from JSON
const RIDDLES: Riddle[] = riddlesData;

// Utility Functions
export const rot13 = (str: string): string => {
  return str.replace(/[a-zA-Z]/g, function (c) {
    const charCode = c.charCodeAt(0);
    const isUpperCase = c <= 'Z';
    const maxCode = isUpperCase ? 90 : 122;
    const newCode = charCode + 13;
    return String.fromCharCode(newCode <= maxCode ? newCode : newCode - 26);
  });
};

export const boardDefault = (): string[][] => {
  return Array(BOARD_ROWS)
    .fill(null)
    .map(() => Array(BOARD_COLS).fill(''));
};

export const getDayNumber = (): number => {
  // Use local midnight to ensure riddle changes at midnight in user's timezone
  const today = new Date();
  const todayLocal = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const originLocal = new Date(ORIGIN_DATE.getFullYear(), ORIGIN_DATE.getMonth(), ORIGIN_DATE.getDate());
  const dateDiff = todayLocal.getTime() - originLocal.getTime();
  return Math.floor(dateDiff / (24 * 60 * 60 * 1000));
};

// Riddle Selection Logic
export const getTodaysRiddle = (): Riddle => {
  const dayNumber = getDayNumber();
  const riddleIndex = dayNumber % RIDDLES.length;
  const riddle = RIDDLES[riddleIndex];

  // Check if the riddle is already decoded (contains common English words)
  const isAlreadyDecoded = (text: string): boolean => {
    const commonWords = ['the', 'and', 'what', 'when', 'where', 'why', 'how', 'this', 'that', 'with', 'from', 'have', 'will', 'can', 'are', 'you', 'me', 'he', 'she', 'it', 'we', 'they'];
    const lowerText = text.toLowerCase();
    return commonWords.some(word => lowerText.includes(word));
  };

  // Only decode if the riddle appears to be encoded
  const shouldDecode = isAlreadyDecoded(riddle.question) === false;

  return {
    ...riddle,
    question: shouldDecode ? rot13(riddle.question) : riddle.question,
    answer: shouldDecode ? rot13(riddle.answer) : riddle.answer,
  };
};

// Word Validation using Dictionary API
export const isValidWord = async (word: string, correctWord?: string): Promise<boolean> => {
  // If we have a correct word, check if the guess matches it
  if (correctWord && word.toLowerCase() === correctWord.toLowerCase()) {
    return true;
  }
  
  // Otherwise check the dictionary
  return await checkWordInDictionary(word);
};

export const isWordLengthValid = (word: string): boolean => {
  return word.length === BOARD_COLS;
};

// Letter Validation Logic
export const validateGuess = (
  guess: string,
  targetWord: string
): LetterValidation[] => {
  const wordLength = targetWord.length;
  const validation: LetterValidation[] = new Array(wordLength).fill('absent');
  const targetLetters = targetWord.toLowerCase().split('');
  const guessLetters = guess.toLowerCase().split('');

  // First pass: mark correct letters
  for (let i = 0; i < wordLength; i++) {
    if (guessLetters[i] === targetLetters[i]) {
      validation[i] = 'correct';
      targetLetters[i] = ''; // Mark as used
    }
  }

  // Second pass: mark present letters
  for (let i = 0; i < wordLength; i++) {
    if (validation[i] !== 'correct') {
      const targetIndex = targetLetters.indexOf(guessLetters[i]);
      if (targetIndex !== -1) {
        validation[i] = 'present';
        targetLetters[targetIndex] = ''; // Mark as used
      }
    }
  }

  return validation;
};

// Game State Logic
export const isGameWon = (validation: LetterValidation[]): boolean => {
  return validation.every(v => v === 'correct');
};

export const isGameOver = (currentAttempt: number, won: boolean): boolean => {
  return won || currentAttempt >= BOARD_ROWS - 1;
};

// Statistics Logic
export const calculateStats = (
  wins: string[],
  played: string[],
  winAttempts: GameState['winAttempt']
): {
  gamesPlayed: number;
  gamesWon: number;
  currentStreak: number;
  maxStreak: number;
  averageGuesses: number;
} => {
  const gamesPlayed = played.length;
  const gamesWon = wins.length;

  // Calculate streaks
  let currentStreak = 0;
  let maxStreak = 0;
  let tempStreak = 0;

  // This is a simplified streak calculation - in reality you'd need to track dates
  for (let i = played.length - 1; i >= 0; i--) {
    if (wins.includes(played[i])) {
      tempStreak++;
      if (i === played.length - 1) {
        currentStreak = tempStreak;
      }
    } else {
      maxStreak = Math.max(maxStreak, tempStreak);
      tempStreak = 0;
    }
  }
  maxStreak = Math.max(maxStreak, tempStreak);

  // Calculate average guesses
  const totalAttempts =
    winAttempts.first +
    winAttempts.second +
    winAttempts.third +
    winAttempts.fourth;
  const averageGuesses = gamesWon > 0 ? totalAttempts / gamesWon : 0;

  return {
    gamesPlayed,
    gamesWon,
    currentStreak,
    maxStreak,
    averageGuesses,
  };
};

// Share Logic
export const generateShareText = (shareData: ShareData): string => {
  const { attempts, won, date } = shareData;
  const gameUrl = 'https://www.seven85design.com/arcade/games/riddl/';

  if (won) {
    return `Riddl ${date}\n\nSolved in ${attempts + 1} attempts! 🎉\n\nPlay at: ${gameUrl}`;
  } else {
    return `Riddl ${date}\n\nFailed to solve 😔\n\nPlay at: ${gameUrl}`;
  }
};

export const generateShareEmoji = (validationGrid: string[][]): string => {
  const emojiMap: Record<string, string> = {
    correct: '🟩', // Changed from purple 🟪 to green 🟩 to match app's teal theme
    present: '🟨',
    absent: '⬜',
  };

  return validationGrid
    .filter(row => row.some(cell => cell !== ''))
    .map(row => row.map(cell => emojiMap[cell] || '⬜').join(''))
    .join('\n');
};

// Keyboard Logic
export const getKeyboardState = (
  validationGrid: string[][]
): Record<string, LetterValidation> => {
  const keyboardState: Record<string, LetterValidation> = {};

  // Process all guesses to determine keyboard state
  validationGrid.forEach(row => {
    row.forEach((validation, index) => {
      if (validation) {
        // This is simplified - in reality you'd need the actual letter
        // For now, we'll use the validation state
        keyboardState[`key-${index}`] = validation as LetterValidation;
      }
    });
  });

  return keyboardState;
};

// Alert Messages
export const getWinMessage = (attempt: number): string => {
  switch (attempt) {
    case 0:
      return 'Wow, So Smart!';
    case 1:
      return 'Amazing Job!';
    case 2:
      return 'Pretty Average';
    case 3:
      return 'Ooo Close Call';
    default:
      return 'You got it!';
  }
};

export const getErrorMessage = (error: string): string => {
  switch (error) {
    case 'not-enough-letters':
      return 'Not enough letters';
    case 'not-in-word-list':
      return 'Not in word list';
    case 'invalid-word':
      return 'Invalid word';
    default:
      return 'Error occurred';
  }
};
